import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Axios from "axios";
import DOMPurify from "dompurify";
import toast from "react-hot-toast";
import UserContext from "../../context/UserContext";
import { buildUserState } from "../../utilities/tokenUtils";
import { useRateLimit } from "../hooks/useRateLimit";

const schema = yup.object().shape({
  email: yup
    .string()
    .required("Email es requerido")
    .email("Ingrese un email válido")
    .max(100, "Email no puede exceder 100 caracteres"),
  password: yup
    .string()
    .required("Contraseña es requerida")
    .min(5, "Contraseña debe tener al menos 5 caracteres"),
});

export default function Login() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const { userData, setUserData } = useContext(UserContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (userData.token) navigate("/");
  }, [navigate, userData.token]);

  const { attempts, isLocked, registerFailure, reset } = useRateLimit("login", 5, 60000);

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    if (isLocked) {
      toast.error("Demasiados intentos fallidos. Espere un momento antes de reintentar.");
      setIsSubmitting(false);
      return;
    }

    try {
      const recaptchaToken = await window.grecaptcha.execute(
        process.env.REACT_APP_RECAPTCHA_SITE_KEY,
        { action: "login" }
      );

      const sanitizedData = {
        email: DOMPurify.sanitize(data.email),
        password: DOMPurify.sanitize(data.password),
        recaptchaToken,
      };

      const loginRes = await Axios.post(
        `${process.env.REACT_APP_BACK_DOMAIN}/users/login`,
        sanitizedData
      );

      if (loginRes.data.setupToken) {
        sessionStorage.setItem("setup-token", loginRes.data.setupToken);
        toast.success("Debe configurar MFA para continuar.");
        reset();
        navigate("/admin/mfa-setup");
      } else if (loginRes.data.partialToken) {
        sessionStorage.setItem("partial-token", loginRes.data.partialToken);
        toast.success("Código MFA enviado. Verifica tu autenticador.");
        reset();
        navigate("/admin/mfa-verify");
      } else {
        setUserData(buildUserState(loginRes.data.token));
        localStorage.setItem("auth-token", loginRes.data.token);
        toast.success("Inicio de sesión exitoso");
        reset();
        navigate("/");
      }
    } catch (err) {
      registerFailure();

      if (attempts >= 4) {
        toast.error("Demasiados intentos fallidos. Espere un momento antes de reintentar.");
      }

      if (err.response?.status === 500) {
        toast.error("Error del servidor. Intente más tarde.");
      } else if (err.response?.data?.msg) {
        toast.error(err.response.data.msg);
      } else {
        toast.error("Error de conexión. Verifique su red.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page">
      <div className="container">
        <h2>Log In</h2>
        <form className="Form Form_login" onSubmit={handleSubmit(onSubmit)}>
          <div className="Form-Container">
            <fieldset>
              <div className="Form-Field">
                <label htmlFor="email" className="Form-Label">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  className="Form-Input"
                  placeholder="Ingrese su email"
                  {...register("email")}
                />
                {errors.email && (
                  <span style={{ color: "red" }}>{errors.email.message}</span>
                )}
              </div>

              <div className="Form-Field">
                <label htmlFor="password" className="Form-Label">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  className="Form-Input"
                  placeholder="Ingrese su contraseña"
                  {...register("password")}
                />
                {errors.password && (
                  <span style={{ color: "red" }}>{errors.password.message}</span>
                )}
              </div>
            </fieldset>

            <input
              type="submit"
              value={isSubmitting ? "Iniciando sesión..." : isLocked ? "Bloqueado" : "Login"}
              className="Button Button_primary"
              disabled={isSubmitting || isLocked}
            />
          </div>
        </form>
      </div>
    </div>
  );
}